import React, { useEffect, useState } from 'react';
import { Pagination } from 'antd';
import Breadcrumb from "../../../../utility/breadcrumb/Breadcrumb";
import { getReviewRating, getUserReviewComment } from '../../../../services/reviewRating';
import ReviewRatingModal from './ReviewRatingModal';
import ReviewCard from './ReviewCard';

const renderStars = (count) => {
  return [...Array(5)].map((_, i) => (
    <i key={i} className={`bi ${i < count ? 'bi-star-fill filled-star' : 'bi-star empty-star'}`}></i>
  ));
};

const ReviewRating = () => {
  const [loading, setLoading] = useState(true);
  const [reviewStats, setReviewStats] = useState(null);
  const [allReviews, setAllReviews] = useState([]);
  const [reviewComments, setReviewComments] = useState([]);
  const [showAddReview, setShowAddReview] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(6);

  const handleOpen = () => setShowAddReview(true);
  const handleClose = () => setShowAddReview(false);

  const fetchReviewStats = async () => {
    setLoading(true);
    try {
      const res = await getReviewRating();
      setReviewStats({
        total_reviews: res?.total_reviews || 0,
        average_rating: parseFloat(res?.average_rating || "0"),
        rating_counts: res?.rating_counts || {},
      });
    } catch (error) {
      console.error("Error loading review stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllReviews = async () => {
    setLoading(true);
    try {
      const res = await getUserReviewComment();
      const mapped = (res || []).map((r) => ({
        id: r.id,
        name: r.user?.full_name || "Anonymous",
        rating: r.rating,
        comment: r.comment,
        date: r.created_at,
        img: "", // Add avatar if available
        reactions: r.reactions || { like_count: 0, dislikes_count: 0 },
      }));
      setAllReviews(mapped);
    } catch (error) {
      console.error("Error loading reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviewStats();
    fetchAllReviews();
  }, []);

  useEffect(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    setReviewComments(allReviews.slice(start, end));
  }, [allReviews, currentPage, pageSize]);

  return (
    <>
      {showAddReview && <ReviewRatingModal onClose={handleClose} />}
      <Breadcrumb />
      <div className="review-rating-wrapper py-3">
        <div className="review-header">
          <h4 className="card-title">Reviews & Average Rating</h4>
          <button className="btn btn-primary add-review-btn" onClick={handleOpen}>
            Add Review
          </button>
        </div>

        <div className="stats-container">
          <div className="stat-box">
            <p className="state-heading">Total Reviews</p>
            <p className="state-heading-star">{reviewStats?.total_reviews}</p>
            <p className="review-date">Based on available data</p>
          </div>

          <div className="stat-box">
            <p className="state-heading">Average Rating</p>
            <div className="modal-stars">
              <p className="state-heading-star">{(reviewStats?.average_rating || 0).toFixed(1)}</p>
              {renderStars(Math.round(reviewStats?.average_rating))}
            </div>
            <p className="review-date">Average rating overall</p>
          </div>

          <div className="stat-box">
            <div className="rating-distribution">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = reviewStats?.rating_counts?.[star] || 0;
                const total = reviewStats?.total_reviews || 1;
                const percent = Math.round((count / total) * 100);
                const colorMap = {
                  5: '#17a58a',
                  4: '#d49ce3',
                  3: '#e4c16f',
                  2: '#4ac4e3',
                  1: '#91512e',
                };
                return (
                  <div key={star} className="rating-bar">
                    <span className="bar-stars">
                      <i className="bi bi-star-fill small-star" /> {star}
                    </span>
                    <div className="bar-wrapper">
                      <div
                        className="bar-fill"
                        style={{ width: `${percent}%`, backgroundColor: colorMap[star] }}
                      ></div>
                    </div>
                    <span className="bar-count">{count}</span>
                    <span className="bar-percent">{percent}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <h4 className="card-title mt-2">Recent Feedbacks</h4>
        <div className="review-list review-grid mt-3">
          {reviewComments.length > 0 ? (
            reviewComments.map((review, index) => (
              <ReviewCard key={index} review={review} />
            ))
          ) : (
            <div className="alert alert-secondary text-center nodatafounddisc" bis_skin_checked="1">No feedback found.</div>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={allReviews.length}
            onChange={(page) => setCurrentPage(page)}
            showSizeChanger={false}
          />
        </div>
      </div>
    </>
  );
};

export default ReviewRating;
